import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'

import Button from '../../../common/Button'
import Modal from '../../../common/Modal'
import ModalInnerWrapper from '../../../common/ModalInnerWrapper'
import PannelBody from '../../../fragments/PannelBody'
import Backdrop from '../../../common/modal/Backdrop'
import FormTextareaBox from '../../../common/interactive/FormTextareaBox'
import { deleteNoteApi } from '../../../../lib/fetchApi'
import { notificationActions } from '../../../../store/notifications'
import { applyDeleteSuccess } from '../../../../lib/applyNoteSoftDelete'
import { isTaskRecord, resolveNoteCaseId } from '../../../../lib/noteDelete'
import { formatDateLocal } from '../../../../lib/script'
import { useWorkQShell } from '../../../work_queues/context/workQShellContext'
import BodySimpleModalV2 from '../../../common/modal/BodySimpleModalV2'
import { useUserTabEditPermissionDisabled } from '../../../../lib/usersPermissions'

import classes from './NotesItem.module.css'

function firstFieldError(result, field) {
    const message = result?.errorMessage
    if (!message || typeof message === 'string') {
        return ''
    }
    const bag = message.errors && typeof message.errors === 'object' ? message.errors : message
    const value = bag?.[field]
    if (Array.isArray(value)) {
        return value.filter(Boolean).join(' ')
    }
    if (typeof value === 'string') {
        return value
    }
    return ''
}

export function DeletedNoteMeta({ itemData }) {
    if (!itemData?.deleted_at) {
        return null
    }

    return (
        <div className={classes.deleted_meta}>
            <div className={classes.deleted_note_label}>{isTaskRecord(itemData) ? 'Deleted task' : 'Deleted note'}</div>
            <div>
                {itemData.deleted_at && <>Deleted {formatDateLocal(itemData.deleted_at)}</>}
                {itemData.deleted_by_user?.name && <> by {itemData.deleted_by_user.name}</>}
            </div>
            {itemData.delete_reason && (
                <div className={classes.deleted_reason}>Reason: {itemData.delete_reason}</div>
            )}
        </div>
    )
}

export default function DeleteNote({ itemData, caseData }) {
    const canDelete = useUserTabEditPermissionDisabled('notes_tasks_delete')
    const dispatch = useDispatch()
    const userToken = useSelector((state) => state.user.token)
    const activeCase = useSelector((state) => state.case.activeCase)
    const shell = useWorkQShell()

    const [loading, setLoading] = useState(false)
    const [modalOpened, setModalOpened] = useState(false)
    const [reason, setReason] = useState('')
    const [reasonError, setReasonError] = useState('')

    const isTask = isTaskRecord(itemData)
    const label = isTask ? 'task' : 'note'

    if (!canDelete) {
        return null
    }

    const openModalHandler = (event) => {
        event.preventDefault()
        event.stopPropagation()
        setReason('')
        setReasonError('')
        setModalOpened(true)
    }

    const closeModalHandler = (event) => {
        event?.preventDefault?.()
        event?.stopPropagation?.()
        setModalOpened(false)
        setReason('')
        setReasonError('')
    }

    const deleteHandler = async (event) => {
        event.preventDefault()
        event.stopPropagation()

        const trimmedReason = reason.trim()
        if (!trimmedReason) {
            setReasonError('Please enter a reason.')
            return
        }
        if (loading) {
            return
        }

        const caseId = resolveNoteCaseId(itemData, caseData, activeCase)
        if (caseId == null || itemData?.id == null) {
            dispatch(notificationActions.setError({ errorMessage: 'Could not delete this item.' }))
            return
        }

        setLoading(true)
        try {
            const result = await deleteNoteApi(userToken, caseId, itemData.id, trimmedReason)
            if (result && result.errorMessage) {
                const reasonMessage = firstFieldError(result, 'reason')
                const noteMessage = firstFieldError(result, 'note')
                if (reasonMessage) {
                    setReasonError(reasonMessage)
                }
                if (noteMessage || !reasonMessage) {
                    dispatch(notificationActions.setError(
                        noteMessage ? { errorMessage: noteMessage } : result
                    ))
                }
                return
            }

            if (result) {
                applyDeleteSuccess(dispatch, result, {
                    caseId,
                    activeCaseId: activeCase?.case?.id,
                    shell,
                })
                dispatch(notificationActions.setError({
                    errorMessage: isTask ? 'Task deleted' : 'Note deleted',
                    type: 'success',
                }))
                closeModalHandler()
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <span className={classes.row_icon_wrap}>
                <BodySimpleModalV2 normalEvent={true} isFlex={true} messages={[`Delete ${label}`]}>
                    <button
                        type="button"
                        className={`${classes.row_icon_button} ${classes.row_icon_button_danger}`}
                        onClick={openModalHandler}
                        aria-label={`Delete ${label}`}
                    >
                        <i className="ti ti-trash" />
                    </button>
                </BodySimpleModalV2>
            </span>
            {modalOpened && (
                <>
                    {ReactDOM.createPortal(
                        <Backdrop hide={closeModalHandler} />,
                        document.getElementById('backdrop-root')
                    )}
                    {ReactDOM.createPortal(
                        <Modal>
                            <ModalInnerWrapper closeModal={closeModalHandler} modalTitle={`Delete ${label}?`}>
                                <PannelBody>
                                    <form onSubmit={deleteHandler}>
                                        <p className="font-14">
                                            {isTask
                                                ? 'This task will stay on the case and can be viewed with Show deleted. It will be removed from the Work Queue and Tasks page.'
                                                : 'This note will stay on the case and can be viewed with Show deleted.'}
                                        </p>
                                        <div className="m-top-12">
                                            <FormTextareaBox
                                                id={`delete_note_reason_${itemData?.id}`}
                                                name="reason"
                                                label="Reason for deletion"
                                                value={reason}
                                                onChange={(e) => {
                                                    setReason(e.target.value)
                                                    if (reasonError) {
                                                        setReasonError('')
                                                    }
                                                }}
                                                required
                                                errorText={reasonError}
                                            />
                                        </div>
                                        <div className="flex-container-equal m-top-24">
                                            <Button
                                                btnStyle="btn_danger"
                                                type="submit"
                                                isLoading={loading}
                                                isDisabled={!reason.trim()}
                                            >
                                                <i className="ti ti-trash" /> Yes, delete
                                            </Button>
                                            <Button click={closeModalHandler} type="button">
                                                Cancel
                                            </Button>
                                        </div>
                                    </form>
                                </PannelBody>
                            </ModalInnerWrapper>
                        </Modal>,
                        document.getElementById('overlay-root')
                    )}
                </>
            )}
        </>
    )
}
